import React, { useState, useEffect } from "react";
import { useJwt } from "react-jwt";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { authService } from "../services/auth";
import { clearProjectState } from "../redux/project/projectSlice";
import { clearBrandState } from "../redux/brand/brandSlice";
import OrbitalLoader from "./common/OrbitalLoader";

const AuthLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-theme-main">
    <OrbitalLoader size={128} special />
  </div>
);

// high level component, wraps most of app
// passes auth params to children components
// upon state changes, re-assesses expiration of access token
const Authentication = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("accessToken"),
  );
  const [isRefreshing, setIsRefreshing] = useState(
    localStorage.getItem("refreshingToken"),
  );
  const [userConfigReady, setUserConfigReady] = useState(
    !localStorage.getItem("accessToken"),
  );
  const [currentUser, setCurrentUser] = useState(() =>
    JSON.parse(localStorage.getItem("currentUser") || "null"),
  );
  const { isExpired } = useJwt(localStorage.getItem("accessToken"));

  useEffect(() => {
    if (accessToken && isExpired) {
      authService.refreshToken(
        (newToken) => {
          setAccessToken(newToken);
        },
        () => {
          console.error("couldnt refresh token");
          window.location = `/login/?next=${window.location.pathname}`;
        },
      );
    }
  }, [accessToken, isExpired]);

  useEffect(() => {
    if (accessToken && !isExpired) {
      setUserConfigReady(false);
      authService.fetchUser(
        (response) => {
          setCurrentUser(response.data);
          setUserConfigReady(true);
        },
        () => {
          setUserConfigReady(true);
        },
      );
    } else if (!accessToken) {
      setUserConfigReady(true);
      setCurrentUser(null);
      dispatch(clearProjectState());
      dispatch(clearBrandState());
    }
  }, [accessToken, isExpired, dispatch]);

  if (accessToken && isExpired) {
    return <AuthLoader />;
  }

  if (accessToken && !isExpired && !userConfigReady) {
    return <AuthLoader />;
  }

  var authState = {
    authenticated: false,
    accessToken: null,
    refreshToken: null,
    refreshingToken: false,
    currentUser: null,
    userConfigReady: false,
    location: location,
  };

  if (accessToken && !isExpired) {
    const storedUser = JSON.parse(
      localStorage.getItem("currentUser") || "null",
    );
    const effectiveUser = storedUser ?? currentUser;

    authState = Object.assign(authState, {
      authenticated: true,
      accessToken: localStorage.getItem("accessToken"),
      refreshToken: localStorage.getItem("refreshToken"),
      currentUser: effectiveUser,
      userConfigReady: userConfigReady,
    });

    const isWelcomeRoute =
      location.pathname === "/welcome" ||
      location.pathname.startsWith("/welcome/");
    const needsWelcome =
      effectiveUser && (effectiveUser.limits?.brands_used ?? 0) === 0;
    if (needsWelcome && !isWelcomeRoute && location.pathname !== "/register/") {
      window.location = "/welcome/";
      return "";
    }
  } else if (accessToken && isExpired) {
    authState = Object.assign(authState, {
      refreshingToken: true,
    });
  }

  return (
    <React.Fragment>
      {children({
        ...authState,
      })}
    </React.Fragment>
  );
};

export default Authentication;
